import styles from './style.postcss';

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import is from 'is_js';
import { List } from 'immutable';
import { Icon, Tooltip, PageCard, Button, connect, testClass } from 'omni-common-ui';
import CardActionIcons from 'components/CardActionIcons';
import CardActionButtons from 'components/CardActionButtons';
import ExpansionButton from 'components/ExpansionButton';
import TableWithExpandablePanel from 'components/TableWithExpandablePanel';
import { actions as OverallDevelopmentActions } from 'data/IndividualDevelopmentPlans/OverallDevelopment';
import { Modes } from '../routes';

const strengthsInfo = {
  type: 'ST',
  name: 'Strengths',
  maxMarkNumber: 3,
  tooltip: 'This section highlights areas in which your child has consistently performed well.',
};
const improvementsMadeInfo = {
  type: 'IM',
  name: 'Improvement made',
  maxMarkNumber: 3,
  tooltip: 'This section highlights areas in which your child has made noticeable improvement.',
};
const needAttentionInfo = {
  type: 'NA',
  name: 'Need attention',
  maxMarkNumber: 2,
  tooltip: 'This section highlights ares which would benefit from continued attention.',
};
const categoryInfo = {
  ST: strengthsInfo,
  IM: improvementsMadeInfo,
  NA: needAttentionInfo,
};

class OverallDevelopment extends PureComponent {

  constructor(props) {
    super(props);
    this._onEditClick = this._onEditClick.bind(this);
    this.onSaveClick = this.onSaveClick.bind(this);
    this._onDiscardClick = this._onDiscardClick.bind(this);
    this.shouldDisableSaveButton = this.shouldDisableSaveButton.bind(this);
    this.setWasEditMade = this.setWasEditMade.bind(this);
    this.wasEditMade = this.wasEditMade.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this.state = {
      wasEditMade: false,
      showMenu: undefined,
      isExpandRemainingMember: {
        [strengthsInfo.type]: false,
        [improvementsMadeInfo.type]: false,
        [needAttentionInfo.type]: false,
      },
    };
  }

  componentDidMount() {
    const {
      fetchOverallDevelopmentResultIfNeeded,
      fetchOverallDevelopmentTemplateIfNeeded,
      idpId,
    } = this.props;
    fetchOverallDevelopmentResultIfNeeded(idpId);
    fetchOverallDevelopmentTemplateIfNeeded(idpId);
    this.props.onComponentMounted(this);
    this._onOverallDevelopmentResultUpdate(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this._onOverallDevelopmentResultUpdate(nextProps);
  }

  _onEditClick() {
    this.props.changeMode(Modes.EDIT_OVERALL_DEVELOPMENT);
  }

  _isEditing() {
    return this.props.isInEditMode;
  }

  setWasEditMade(wasEditMade) {
    if (is.not.boolean(wasEditMade)) return;
    this.setState({ wasEditMade });
  }

  wasEditMade() {
    return this.props.isInEditMode && this.state.wasEditMade;
  }

  shouldDisableSaveButton() {
    const { overallDevelopment } = this.state;
    const template = this.props.overallDevelopment &&
      this.props.overallDevelopment.get('template');
    return ! List.isList(overallDevelopment) ||
      overallDevelopment.size === 0 ||
      ! is.existy(template) ||
      (this._isNeededToChoose(strengthsInfo.type, template.strengths)) ||
      (this._isNeededToChoose(needAttentionInfo.type, template.needAttentions) ||
      (this._isNeededToChoose(improvementsMadeInfo.type, template.improvementsModes)));
  }

  _onOverallDevelopmentResultUpdate(props) {
    if (this.wasEditMade()) return;
    const overallDevelopmentResult = props.overallDevelopment && props.overallDevelopment.get('result');
    const items = is.not.existy(overallDevelopmentResult) ?
      new List() :
      new List(overallDevelopmentResult.items.toArray());
    this.setState({
      overallDevelopment: items,
    });
  }

  onSaveClick() {
    const { overallDevelopment } = this.state;
    const { idpId, groupIdpId, saveOverallDevelopment } = this.props;
    const template = this.props.overallDevelopment.get('template');
    const validResults = overallDevelopment.filter((result) =>
      template.strengths.some((st) => st.items.some((tp) =>
        result.typeCode === strengthsInfo.type &&
        result.unitId === tp.unitId &&
        result.subSkillId === tp.id)) ||
      template.improvementsModes.some((st) => st.items.some((tp) =>
        result.typeCode === improvementsMadeInfo.type &&
        result.unitId === tp.unitId &&
        result.subSkillId === tp.id)) ||
      template.needAttentions.some((st) => st.items.some((tp) =>
        result.typeCode === needAttentionInfo.type &&
        result.unitId === tp.unitId &&
        result.subSkillId === tp.id)));

    saveOverallDevelopment(idpId, validResults, groupIdpId)
      .then(() => this._onDiscardClick());
  }

  _onDiscardClick(props = this.props) {
    this.setWasEditMade(false);
    props.changeMode('');
    this._onOverallDevelopmentResultUpdate(props);
  }

  _getMenuItemDescription(ability) {
    if (is.not.existy(ability)) return '';
    if (ability.point === '3') return 'The student can consistently ...';
    if (ability.point === '2') return 'The student can sometimes ...';

    return '';
  }

  _getButtonTypeForAbility(originalAbility, currentAbility) {
    const ability = currentAbility === undefined ? originalAbility : currentAbility;
    if (ability.point === '1') return Button.Type.wrong;
    if (ability.point === '3') return Button.Type.correct;
    if (ability.point === '2') return Button.Type.needsAttention;

    return Button.Type.black;
  }

  _isItemCurrentlySelected(type, item) {
    const { overallDevelopment } = this.state;
    return overallDevelopment.some((development) =>
      type === development.typeCode &&
      development.unitId === item.unitId &&
      development.subSkillId === item.id);
  }

  onItemClick(event, skillId, item, type) {
    event.preventDefault();
    let result;
    if (is.existy(this.state.showMenu)) result = this.closeMenu();
    const { overallDevelopment } = this.state;
    const index = overallDevelopment.findIndex((development) =>
      type === development.typeCode &&
      development.unitId === item.unitId &&
      development.subSkillId === item.id);
    const isShowMenu = index < 0 && type === improvementsMadeInfo.type;
    if (is.not.existy(result)) result = overallDevelopment;
    if (index >= 0) {
      result = result.remove(index);
    } else {
      result = result.push({
        typeCode: type,
        unitId: item.unitId,
        skillId,
        subSkillId: item.id,
        originalAbilityId: item.abilityId,
        improvedAbilityId: undefined,
      });
    }
    this.setState({
      overallDevelopment: result,
      showMenu: isShowMenu ? { unitId: item.unitId, itemId: item.id } : undefined,
    }, () => {
      isShowMenu ? document.addEventListener('click', this.closeMenu) : undefined;
    });
    this.setWasEditMade(true);
  }

  closeMenu() {
    const { overallDevelopment, showMenu } = this.state;
    let results = overallDevelopment;
    const index = results.findIndex((result) =>
      result.typeCode === 'IM' &&
      result.subSkillId === showMenu.itemId &&
      result.unitId === showMenu.unitId);
    const currentResult = results.get(index);
    if (is.existy(currentResult) && is.not.existy(currentResult.ImprovedAbilityId)) {
      results = results.remove(index);
    }

    this.setState({
      showMenu: undefined,
      overallDevelopment: results,
    }, () => {
      document.removeEventListener('click', this.closeMenu);
    });

    return results;
  }

  _onItemMarked(skillId, item, ability, type) {
    const { overallDevelopment } = this.state;
    const index = overallDevelopment.findIndex((development) =>
      type === development.typeCode &&
      development.unitId === item.unitId &&
      development.subSkillId === item.id);
    let result = overallDevelopment;
    if (index >= 0) {
      result = result.remove(index);
    }

    result = result.push({
      typeCode: type,
      unitId: item.unitId,
      skillId,
      subSkillId: item.id,
      originalAbilityId: item.abilityId,
      improvedAbilityId: ability.id,
    });

    this.setState({
      overallDevelopment: result,
      showMenu: undefined,
    }, () => {
      document.removeEventListener('click', this.closeMenu);
    });
  }

  _isEditItemDisabled(type, item) {
    const { overallDevelopment } = this.state;
    const maxMarkNumber = categoryInfo[type].maxMarkNumber;
    const selectedResults = type === improvementsMadeInfo.type ?
      overallDevelopment.filter((result) => type === result.typeCode &&
        is.existy(result.improvedAbilityId)) :
      overallDevelopment.filter((result) => type === result.typeCode);
    const isSelectedInCurrentArea = selectedResults.some((result) =>
      result.unitId === item.unitId && result.subSkillId === item.id);
    const isSelectedInOtherArea = overallDevelopment.some((od) =>
      od.typeCode !== type &&
      od.unitId === item.unitId &&
      od.subSkillId === item.id);

    return isSelectedInOtherArea ||
      (! isSelectedInCurrentArea &&
        selectedResults.size >= maxMarkNumber);
  }

  _renderCell(type, skillTemplate, column, results) {
    const items = skillTemplate.items.filter((item) => item.unitId === column);
    return <div className={styles.OverallDevelopment_editCard_cell}>
      {
        items.size === 0 ? null : items.map((item) => {
          const originalAbility = skillTemplate.abilities
            .filter((ability) => ability.id === item.abilityId).first();
          const itemResult = results.filter((result) =>
            result.unitId === item.unitId && result.subSkillId === item.id).first();
          const currentAbility = itemResult && skillTemplate.abilities
            .filter((ability) => itemResult.improvedAbilityId === ability.id).first();

          return <div className={styles.OverallDevelopment_editCard_cell_item}>
            <Button type={this._getButtonTypeForAbility(originalAbility, currentAbility)}
                className={this._isItemCurrentlySelected(type, item) && styles.__buttonActived}
                block
                disabled={this._isEditItemDisabled(type, item)}
                onClickActiveClassRemoveDelay={0}
                active={this._isItemCurrentlySelected(type, item)}
                onClick={(event) =>
                  this.onItemClick(event, skillTemplate.id, item, type)} >
              <span>{item.name}</span>
            </Button>
            {
              type === 'IM' &&
                is.existy(this.state.showMenu) &&
                this.state.showMenu.itemId === item.id &&
                this.state.showMenu.unitId === item.unitId &&
                <div className={styles.OverallDevelopment_editCard_cell_menu}>
                  {
                    skillTemplate.abilities.filter((ability) =>
                      ability.point > originalAbility.point).map((ability) =>
                        <div>
                          <Button type={this._getButtonTypeForAbility(ability)}
                              block
                              onClick={() =>
                                this._onItemMarked(skillTemplate.id, item, ability, type)}>
                            <span>{this._getMenuItemDescription(ability)}</span>
                            <Icon id={this._getIconIdByAbility(ability)}
                                className={this._getIconClassNameByAbility(ability)} />
                          </Button>
                        </div>)
                  }
                </div>
            }
          </div>;
        })
      }
    </div>;
  }

  _renderExpandButton(type) {
    const { isExpandRemainingMember } = this.state;

    return <ExpansionButton isExpand={isExpandRemainingMember[type]}
        onExpansionButtonClick={
          () => {
            const copy = { ...isExpandRemainingMember };
            copy[type] = ! copy[type];
            this.setState({ isExpandRemainingMember: copy });
          }
        }
        className={styles.OverallDevelopment_editCard_expand} />;
  }

  _renderTable(type, skillTemplates, results, units) {
    return <TableWithExpandablePanel rows={skillTemplates}
        onlyRowDivider={false}>
      <TableWithExpandablePanel.Panel>
        <TableWithExpandablePanel.Header>
          <TableWithExpandablePanel.Cell columnId="skill-name" />
        </TableWithExpandablePanel.Header>
        <TableWithExpandablePanel.Body>
          {
            (skillTemplate, columnId) => {
              switch (columnId) {
                case 'skill-name':
                  return <span>{skillTemplate.name}</span>;
                default:
                  throw new Error(`Invalid column ${columnId}`);
              }
            }
          }
        </TableWithExpandablePanel.Body>
      </TableWithExpandablePanel.Panel>
      <TableWithExpandablePanel.Header>
        <TableWithExpandablePanel.Row>
          {
            units.sortBy((unit) => unit.name).reverse().map((unit) =>
              <TableWithExpandablePanel.Cell key={unit.id} columnId={unit.id} small>
                <div className={styles.OverallDevelopment_table_header}>{unit.name}</div>
              </TableWithExpandablePanel.Cell>)
          }
        </TableWithExpandablePanel.Row>
      </TableWithExpandablePanel.Header>
      <TableWithExpandablePanel.Body>
        {(skillTemplate, column) => this._renderCell(type, skillTemplate, column, results)}
      </TableWithExpandablePanel.Body>
    </TableWithExpandablePanel>;
  }

  _getIconIdByAbility(ability) {
    if (is.not.existy(ability)) return '';
    if (ability.point === '3') return 'happy';
    else if (ability.point === '2') return 'smile';

    return 'unhappy';
  }

  _getIconClassNameByAbility(ability) {
    if (is.not.existy(ability)) return undefined;
    if (ability.point === '3') return styles.OverallDevelopment_happyIcon;
    if (ability.point === '2') return styles.OverallDevelopment_smileIcon;
    if (ability.point === '1') return styles.OverallDevelopment_unhappyIcon;

    return undefined;
  }

  _getIconIdByType(type) {
    if (type === strengthsInfo.type) return 'happy';
    if (type === needAttentionInfo.type) return 'unhappy';

    return '';
  }

  _getIconClassNameByType(type) {
    if (type === strengthsInfo.type) return styles.OverallDevelopment_happyIcon;
    if (type === needAttentionInfo.type) return styles.OverallDevelopment_unhappyIcon;

    return undefined;
  }

  _renderViewSkillItem(type, item, result, abilities) {
    const originalAbility = abilities.filter((a) => a.id === result.originalAbilityId).first();
    const improvedAbility = abilities.filter((a) => a.id === result.improvedAbilityId).first();
    return <div className={styles.OverallDevelopment_viewCard_item}>
      <Button type={Button.Type.black}
          block={false}
          disabled
          onClickActiveClassRemoveDelay={0}>
        <span>{item.name}</span>
      </Button>
      {
        type === improvementsMadeInfo.type && is.existy(result) && <div>
          <Icon id={this._getIconIdByAbility(originalAbility)}
              className={this._getIconClassNameByAbility(originalAbility)} />
          <Icon id="chevron-small-right" />
          <Icon id={this._getIconIdByAbility(improvedAbility)}
              className={this._getIconClassNameByAbility(improvedAbility)} />
        </div>
      }
    </div>;
  }

  _renderViewContent(type, skillTemplates, results) {
    const categoryResults = results.filter((result) => result.typeCode === type);
    if (! (List.isList(skillTemplates) &&
      skillTemplates.size > 0 &&
      categoryResults.size > 0)) return null;

    return <div className={styles.OverallDevelopment_viewCard}>
      <h1>{categoryInfo[type].name}</h1>
      <Icon id={this._getIconIdByType(type)} className={this._getIconClassNameByType(type)} />
      {skillTemplates.map((skillTemplate) => {
        const items = skillTemplate.items.filter((i) =>
          categoryResults.some((r) => r.unitId === i.unitId && r.subSkillId === i.id));
        if (items.size === 0) return null;
        return <div>
          <h2>{skillTemplate.name}</h2>
          <div>
            {items.map((item) => {
              const result = categoryResults.filter((cr) =>
                cr.unitId === item.unitId && cr.subSkillId === item.id).first();
              return this._renderViewSkillItem(type, item, result, skillTemplate.abilities);
            })}
          </div>
        </div>;
      })}
    </div>;
  }

  _isNeededToChoose(type, skillTemplates) {
    if (skillTemplates.size === 0) return false;

    const { overallDevelopment } = this.state;
    const categoryResults = overallDevelopment.filter((r) => r.typeCode === type);

    const hasCanMarkedItems = skillTemplates.some((template) =>
      template.items.filter((item) =>
        ! overallDevelopment.some((od) =>
          od.typeCode !== type &&
          od.unitId === item.unitId &&
          od.subSkillId === item.id)).size > 0);

    if (hasCanMarkedItems) {
      return categoryResults.size === 0 ||
        (improvementsMadeInfo.type === type &&
          ! categoryResults.some((r) => is.existy(r.improvedAbilityId)));
    }

    return false;
  }

  _renderEditContent(type, skillTemplates, results, units) {
    const categoryResults = results.filter((result) => result.typeCode === type);
    if (! (List.isList(skillTemplates) && skillTemplates.size > 0)) return null;
    const defaultDisplayNumber = 1;
    let displayedTemplates = skillTemplates.filter((_, index) => index < defaultDisplayNumber);
    if (this.state.isExpandRemainingMember[type]) {
      displayedTemplates = skillTemplates;
    }

    return <PageCard className={styles.OverallDevelopment_editCard}>
      <PageCard.Heading text={`${categoryInfo[type].name} ${categoryResults.size}/${categoryInfo[type].maxMarkNumber}`}
          className={styles.OverallDevelopment_editCard_header}
          titleClassName={styles.OverallDevelopment_editCard_title}>
        <Tooltip text={categoryInfo[type].tooltip}
            placement="right"
            trigger={['hover']}
            className={styles.OverallDevelopment_editCard_tooltip}>
          <Icon id="question" />
        </Tooltip>
        { this._isNeededToChoose(type, skillTemplates) && <span>Choose at least one item.</span>}
      </PageCard.Heading>
      <PageCard.Content className={styles.OverallDevelopment_editCard_content}>
        {this._renderTable(type, displayedTemplates, categoryResults, units)}
      </PageCard.Content>
      {this._renderExpandButton(type)}
    </PageCard>;
  }

  _renderContent() {
    const template = this.props.overallDevelopment &&
      this.props.overallDevelopment.get('template');
    const { overallDevelopment } = this.state;
    if (is.not.existy(template) || is.not.existy(overallDevelopment)) return null;
    const units = template.units;
    if (! this._isEditing()) {
      return <div>
        {this._renderViewContent(strengthsInfo.type,
          template.strengths,
          overallDevelopment)}
        {overallDevelopment.size > 0 &&
          template.strengths.size > 0 &&
            template.improvementsModes.size > 0 &&
              <div className={styles.OverallDevelopment_separatorLine} />}
        {this._renderViewContent(improvementsMadeInfo.type,
          template.improvementsModes,
          overallDevelopment)}
        {overallDevelopment.size > 0 &&
          template.needAttentions.size > 0 &&
            template.improvementsModes.size > 0 &&
              <div className={styles.OverallDevelopment_separatorLine} />}
        {this._renderViewContent(needAttentionInfo.type,
          template.needAttentions,
          overallDevelopment)}
      </div>;
    }

    return <div>
      {this._renderEditContent(strengthsInfo.type,
        template.strengths,
        overallDevelopment,
        units)}
      {this._renderEditContent(improvementsMadeInfo.type,
        template.improvementsModes,
        overallDevelopment,
        units)}
      {this._renderEditContent(needAttentionInfo.type,
        template.needAttentions,
        overallDevelopment,
        units)}
    </div>;
  }

  _renderButtons() {
    if (! this._isEditing()) {
      return null;
    }
    return <CardActionButtons className={styles.OverallDevelopment_buttons}
        isPrimaryDisabled={this.shouldDisableSaveButton()}
        onPrimaryClick={this.onSaveClick}
        onSecondaryClick={this._onDiscardClick} />;
  }

  _renderEmptyHint() {
    if (this.props.isInEditMode) return null;

    const { overallDevelopment } = this.state;
    const template = this.props.overallDevelopment &&
      this.props.overallDevelopment.get('template');
    if (! List.isList(overallDevelopment) || ! is.existy(template)) {
      return null;
    }

    let emptyActionsNumber = 0;
    if (this._isNeededToChoose(strengthsInfo.type, template.strengths)) {
      emptyActionsNumber += 1;
    }
    if (this._isNeededToChoose(needAttentionInfo.type, template.needAttentions)) {
      emptyActionsNumber += 1;
    }
    if (this._isNeededToChoose(improvementsMadeInfo.type, template.improvementsModes)) {
      emptyActionsNumber += 1;
    }

    if (emptyActionsNumber === 0) return null;

    let hint = '';
    if (emptyActionsNumber === 1) {
      hint = `${emptyActionsNumber} action is missing.`;
    } else if (emptyActionsNumber > 1) {
      hint = `${emptyActionsNumber} actions are missing.`;
    }

    return <div className={styles.OverallDevelopment_HeaderHint}>
      {hint}
    </div>;
  }

  render() {
    if (! this.state || ! is.existy(this.state.overallDevelopment)) {
      return null;
    }
    return <PageCard className={testClass('overall-development')}>
      <PageCard.Heading text="Overall development" stackMode="horizontal">
        {this._renderEmptyHint()}
        <CardActionIcons hideEditIcon={! this.props.canEdit}
            onEditClick={this._onEditClick}
            hideDeleteIcon />
      </PageCard.Heading>
      <PageCard.Content className={styles.OverallDevelopment}>
        {this._renderContent()}
        {this._renderButtons()}
      </PageCard.Content>
    </PageCard>;
  }
}

OverallDevelopment.propTypes = {
  studentInfo: PropTypes.object,
  idpId: PropTypes.string,
  groupIdpId: PropTypes.string,
  saveOverallDevelopment: PropTypes.func,
  fetchOverallDevelopmentResultIfNeeded: PropTypes.func,
  fetchOverallDevelopmentTemplateIfNeeded: PropTypes.func,
  overallDevelopment: PropTypes.object,
  changeMode: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  isInEditMode: PropTypes.bool,
  onComponentMounted: PropTypes.func.isRequired,
};

function mapStateToProps(state, ownProps) {
  const { idpId } = ownProps;
  const overallDevelopment = state.get('rootReducer').get('overallDevelopment').get(idpId);
  return {
    overallDevelopment,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(OverallDevelopmentActions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(OverallDevelopment);



// WEBPACK FOOTER //
// ./containers/Group/IndividualDevelopmentPlan/IndividualDevelopmentPlanDetailView/OverallDevelopment/index.jsx